import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, Search, Eye, Printer, Download, X, CreditCard, CheckCircle, Clock, AlertCircle, RotateCcw, ExternalLink, History, ArrowRight, Link, Grid, Upload, FileSpreadsheet, Warehouse, Package } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { useIsAdmin } from '../context/AuthContext';
import VariantGridSelector from '../components/VariantGridSelector';
import QuickAddProduct from '../components/QuickAddProduct';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CREDIT_NOTE_REASONS = [
  { value: 'Exchange - Size change', label: 'Exchange – Size change' },
  { value: 'Defective implant', label: 'Defective implant' },
  { value: 'Customer return', label: 'Customer return' },
  { value: 'Pricing correction', label: 'Pricing correction' },
  { value: 'Damaged in transit', label: 'Damaged in transit' },
  { value: 'Wrong product shipped', label: 'Wrong product shipped' },
  { value: 'Quality issue', label: 'Quality issue' },
  { value: 'Other', label: 'Other' }
];

const PaymentStatusBadge = ({ status }) => {
  const config = {
    paid: { icon: CheckCircle, label: 'Paid', className: 'status-paid' },
    pending: { icon: Clock, label: 'Pending', className: 'status-pending' },
    partial: { icon: AlertCircle, label: 'Partial', className: 'status-partial' }
  };
  
  const { icon: Icon, label, className } = config[status] || config.pending;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const Sales = () => {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);

  // Inventory picker state
  const [isInventoryPickerOpen, setIsInventoryPickerOpen] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [pickerSelections, setPickerSelections] = useState({});
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const invoiceRef = useRef(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12, buyer_item_code: '' }],
    payment_status: 'pending',
    payment_date: '',
    payment_method: '',
    payment_notes: ''
  });

  const [paymentData, setPaymentData] = useState({
    payment_status: 'pending',
    payment_date: '',
    payment_method: '',
    payment_notes: ''
  });

  // Credit Note states
  const [isCreditNoteDialogOpen, setIsCreditNoteDialogOpen] = useState(false);
  const [isViewCreditNotesOpen, setIsViewCreditNotesOpen] = useState(false);
  const [selectedSaleForCN, setSelectedSaleForCN] = useState(null);
  const [invoiceCreditNotes, setInvoiceCreditNotes] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [cnFormData, setCnFormData] = useState({
    items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }],
    reason: 'Customer return',
    notes: ''
  });

  // Variant Grid Selector states
  const [isGridSelectorOpen, setIsGridSelectorOpen] = useState(false);
  const [gridSelectorItemIndex, setGridSelectorItemIndex] = useState(null);

  // Excel import states
  const fileInputRef = useRef(null);
  const [importErrors, setImportErrors] = useState([]);
  const [showImportErrors, setShowImportErrors] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Check for create=true query param to auto-open dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true' && !loading) {
      resetForm();
      
      // Check if items were passed from Inventory page
      const inventoryItems = location.state?.inventoryItems;
      if (inventoryItems && inventoryItems.length > 0) {
        setFormData(prev => ({
          ...prev,
          items: inventoryItems
        }));
      }
      
      setIsDialogOpen(true);
      // Clear the query param and state
      setSearchParams({});
      window.history.replaceState({}, document.title);
    }
  }, [searchParams, loading]);

  const fetchData = async () => {
    try {
      const [salesRes, customersRes, productsRes, inventoryRes] = await Promise.all([
        axios.get(`${API_URL}/sales`),
        axios.get(`${API_URL}/customers`),
        axios.get(`${API_URL}/products`),
        axios.get(`${API_URL}/inventory`)
      ]);
      setSales(salesRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (index, productId) => {
    const product = products.find(p => p.id === productId);
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      product_id: productId,
      selling_price: product?.selling_price || '',
      gst_percentage: product?.gst_percentage || 12,
      batch_number: '',
      // Auto-populate buyer_item_code from the Product master. Manual edits
      // afterwards win — updateItem() overwrites this value without touching
      // it again on subsequent product re-select.
      buyer_item_code: product?.buyer_item_code || ''
    };
    setFormData({ ...formData, items: newItems });
  };

  // Handle selection from variant grid
  const handleGridSelect = (product) => {
    if (gridSelectorItemIndex !== null) {
      const newItems = [...formData.items];
      newItems[gridSelectorItemIndex] = {
        ...newItems[gridSelectorItemIndex],
        product_id: product.id,
        selling_price: product.selling_price || '',
        gst_percentage: product.gst_percentage || 12,
        batch_number: '',
        buyer_item_code: product.buyer_item_code || ''
      };
      setFormData({ ...formData, items: newItems });
    }
  };

  const openGridSelector = (index) => {
    setGridSelectorItemIndex(index);
    setIsGridSelectorOpen(true);
  };

  const getAvailableBatches = (productId) => {
    return inventory.filter(inv => inv.product_id === productId && inv.quantity > 0);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12, buyer_item_code: '' }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  // Quick Add Product handler
  const handleQuickAddProduct = useCallback((productData) => {
    setFormData(prev => {
      // Check if we have an empty first item
      const hasEmptyFirstItem = prev.items.length === 1 && 
        !prev.items[0].product_id && 
        !prev.items[0].quantity;

      let newItems;
      if (hasEmptyFirstItem) {
        // Replace the empty first item
        newItems = [{
          product_id: productData.product_id,
          batch_number: productData.batch_number,
          quantity: productData.quantity || '',
          selling_price: productData.selling_price,
          gst_percentage: productData.gst_percentage
        }];
      } else {
        // Add new item
        newItems = [...prev.items, {
          product_id: productData.product_id,
          batch_number: productData.batch_number,
          quantity: productData.quantity || '',
          selling_price: productData.selling_price,
          gst_percentage: productData.gst_percentage
        }];
      }

      // Focus on quantity field after state updates
      setTimeout(() => {
        const lastIndex = newItems.length - 1;
        const qtyInput = document.querySelector(`[data-testid="item-qty-${lastIndex}"]`);
        if (qtyInput) {
          qtyInput.focus();
          qtyInput.select();
        }
      }, 100);

      return { ...prev, items: newItems };
    });
  }, []);

  // Get stock for a product/batch
  const getStockForBatch = useCallback((productId, batchNumber) => {
    const inv = inventory.find(i => i.product_id === productId && i.batch_number === batchNumber);
    return inv?.quantity || 0;
  }, [inventory]);

  // Get total stock for a product
  const getTotalStock = useCallback((productId) => {
    return inventory
      .filter(i => i.product_id === productId)
      .reduce((sum, i) => sum + (i.quantity || 0), 0);
  }, [inventory]);

  // Inventory picker helpers
  const availableInventory = inventory.filter(i => i.quantity > 0);
  const filteredPickerInventory = availableInventory.filter(i =>
    inventorySearch === '' ||
    i.product_name.toLowerCase().includes(inventorySearch.toLowerCase()) ||
    i.brand.toLowerCase().includes(inventorySearch.toLowerCase()) ||
    i.batch_number.toLowerCase().includes(inventorySearch.toLowerCase())
  );

  const pickerKey = (inv) => `${inv.product_id}|${inv.batch_number}`;

  const togglePickerItem = (inv) => {
    const key = pickerKey(inv);
    setPickerSelections(prev => {
      if (key in prev) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { inv, qty: Math.min(1, inv.quantity) } };
    });
  };

  const updatePickerQty = (inv, qty) => {
    const key = pickerKey(inv);
    const val = Math.max(1, Math.min(parseInt(qty) || 1, inv.quantity));
    setPickerSelections(prev => ({ ...prev, [key]: { inv, qty: val } }));
  };

  const applyPickerSelections = () => {
    const items = Object.values(pickerSelections).map(({ inv, qty }) => ({
      product_id: inv.product_id,
      batch_number: inv.batch_number,
      quantity: qty.toString(),
      selling_price: inv.selling_price || products.find(p => p.id === inv.product_id)?.selling_price || '',
      gst_percentage: inv.gst_percentage || products.find(p => p.id === inv.product_id)?.gst_percentage || 12
    }));

    if (items.length === 0) {
      toast.error('Select at least one item');
      return;
    }

    setFormData(prev => {
      const hasEmptyFirst = prev.items.length === 1 && !prev.items[0].product_id;
      return { ...prev, items: hasEmptyFirst ? items : [...prev.items, ...items] };
    });

    toast.success(`Added ${items.length} item(s) from inventory`);
    setIsInventoryPickerOpen(false);
    setPickerSelections({});
    setInventorySearch('');
  };

  // Inline stock validation
  const getStockWarning = useCallback((productId, batchNumber, quantity) => {
    if (!productId || !batchNumber || !quantity) return null;
    const stock = getStockForBatch(productId, batchNumber);
    const qty = parseInt(quantity);
    if (isNaN(qty)) return null;
    if (qty > stock) return { type: 'exceed', stock, qty };
    if (qty === stock) return { type: 'exact', stock, qty };
    return null;
  }, [getStockForBatch]);

  // Download Excel template for invoice import
  const downloadInvoiceTemplate = () => {
    const template = [
      { 
        'Product Name': 'Dental Implant', 
        'Brand': 'Adin',
        'Size': '3.75 x 10', 
        'Batch Number': 'BATCH-001', 
        'Quantity': 2, 
        'Selling Price': 5000,
        'GST %': 12
      },
      { 
        'Product Name': 'Dental Implant', 
        'Brand': 'Adin',
        'Size': '4.2 x 11.5', 
        'Batch Number': 'BATCH-002', 
        'Quantity': 1, 
        'Selling Price': 5500,
        'GST %': 12
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Product Name
      { wch: 15 }, // Brand
      { wch: 12 }, // Size
      { wch: 15 }, // Batch Number
      { wch: 10 }, // Quantity
      { wch: 15 }, // Selling Price
      { wch: 8 }   // GST %
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice Items');
    XLSX.writeFile(wb, 'invoice_import_template.xlsx');
    toast.success('Template downloaded');
  };

  // Handle Excel import for invoice items
  const handleExcelImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);

        if (rows.length === 0) {
          toast.error('No data found in Excel file');
          return;
        }

        const importedItems = [];
        const errors = [];

        rows.forEach((row, rowIndex) => {
          const productName = row['Product Name'] || row['product_name'] || row['Product'] || '';
          const brand = row['Brand'] || row['brand'] || '';
          const size = row['Size'] || row['size'] || '';
          const batchNumber = row['Batch Number'] || row['batch_number'] || row['Batch'] || row['batch'] || '';
          const quantity = parseInt(row['Quantity'] || row['quantity'] || row['Qty'] || row['qty'] || 0);
          const sellingPrice = parseFloat(row['Selling Price'] || row['selling_price'] || row['Price'] || row['price'] || 0);
          const gstPercentage = parseFloat(row['GST %'] || row['GST'] || row['gst_percentage'] || row['gst'] || 0);

          // Find matching product
          let product = null;
          
          // Try to match by brand + size (most specific)
          if (brand && size) {
            product = products.find(p => 
              p.brand && p.brand.toLowerCase() === brand.toLowerCase() &&
              p.size && p.size.toLowerCase() === size.toLowerCase()
            );
          }
          
          // Try to match by name + brand + size
          if (!product && productName && brand && size) {
            product = products.find(p => 
              p.name.toLowerCase().includes(productName.toLowerCase()) &&
              p.brand && p.brand.toLowerCase() === brand.toLowerCase() &&
              p.size && p.size.toLowerCase() === size.toLowerCase()
            );
          }
          
          // Try to match by name + size
          if (!product && productName && size) {
            product = products.find(p => 
              p.name.toLowerCase().includes(productName.toLowerCase()) &&
              p.size && p.size.toLowerCase() === size.toLowerCase()
            );
          }
          
          // Try to match by size only
          if (!product && size) {
            product = products.find(p => p.size && p.size.toLowerCase() === size.toLowerCase());
          }
          
          // Try to match by name + brand
          if (!product && productName && brand) {
            product = products.find(p => 
              p.name.toLowerCase().includes(productName.toLowerCase()) &&
              p.brand && p.brand.toLowerCase() === brand.toLowerCase()
            );
          }
          
          // Try to match by name only
          if (!product && productName) {
            product = products.find(p => p.name.toLowerCase().includes(productName.toLowerCase()));
          }

          if (!product) {
            errors.push({
              row: rowIndex + 2,
              type: 'product_not_found',
              message: `Product not found: "${productName}" ${brand ? `[${brand}]` : ''} ${size ? `(${size})` : ''}`
            });
            return;
          }

          if (!batchNumber) {
            errors.push({
              row: rowIndex + 2,
              type: 'missing_batch',
              message: `Missing batch number for: ${product.name} ${product.size || ''}`
            });
            return;
          }

          if (!quantity || quantity <= 0) {
            errors.push({
              row: rowIndex + 2,
              type: 'invalid_quantity',
              message: `Invalid quantity for: ${product.name} ${product.size || ''}`
            });
            return;
          }

          // Check stock availability
          const availableStock = getStockForBatch(product.id, batchNumber);
          
          if (availableStock <= 0) {
            // Check if batch exists at all
            const batchExists = inventory.some(i => i.batch_number === batchNumber);
            if (!batchExists) {
              errors.push({
                row: rowIndex + 2,
                type: 'batch_not_found',
                message: `Batch "${batchNumber}" not found for: ${product.name} ${product.size || ''}`
              });
            } else {
              errors.push({
                row: rowIndex + 2,
                type: 'no_stock',
                message: `No stock for batch "${batchNumber}": ${product.name} ${product.size || ''}`
              });
            }
            return;
          }

          if (quantity > availableStock) {
            errors.push({
              row: rowIndex + 2,
              type: 'insufficient_stock',
              message: `Insufficient stock for: ${product.name} ${product.size || ''} (Requested: ${quantity}, Available: ${availableStock})`
            });
            return;
          }

          // Valid item - add to import list
          importedItems.push({
            product_id: product.id,
            batch_number: batchNumber,
            quantity: quantity.toString(),
            selling_price: sellingPrice || product.selling_price || '',
            gst_percentage: gstPercentage || product.gst_percentage || 12
          });
        });

        // Update form with imported items
        if (importedItems.length > 0) {
          setFormData(prev => {
            // Check if first item is empty
            const hasEmptyFirstItem = prev.items.length === 1 && 
              !prev.items[0].product_id && 
              !prev.items[0].batch_number;

            return {
              ...prev,
              items: hasEmptyFirstItem ? importedItems : [...prev.items, ...importedItems]
            };
          });
          
          toast.success(`Imported ${importedItems.length} item(s) successfully`);
        }

        // Show errors if any
        if (errors.length > 0) {
          setImportErrors(errors);
          setShowImportErrors(true);
          if (importedItems.length === 0) {
            toast.error(`Import failed: ${errors.length} error(s) found`);
          } else {
            toast.warning(`${errors.length} item(s) skipped due to errors`);
          }
        }

      } catch (error) {
        console.error('Excel parse error:', error);
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend stock validation
    const stockErrors = [];
    formData.items.forEach((item, idx) => {
      if (item.product_id && item.batch_number && item.quantity) {
        const stock = getStockForBatch(item.product_id, item.batch_number);
        const qty = parseInt(item.quantity);
        if (qty > stock) {
          const product = products.find(p => p.id === item.product_id);
          stockErrors.push(`Item #${idx + 1} (${product?.name || 'Unknown'}): need ${qty}, only ${stock} available`);
        }
      }
    });
    
    if (stockErrors.length > 0) {
      toast.error(`Insufficient stock: ${stockErrors[0]}`, { duration: 5000 });
      return;
    }
    
    try {
      const payload = {
        customer_id: formData.customer_id,
        invoice_date: formData.invoice_date,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          batch_number: item.batch_number,
          quantity: parseInt(item.quantity),
          selling_price: parseFloat(item.selling_price),
          gst_percentage: parseFloat(item.gst_percentage),
          // Only send when the operator has provided/kept a code. Empty string
          // becomes null so the backend still applies the Product-master fallback.
          buyer_item_code: (item.buyer_item_code || '').trim() || null
        })),
        payment_status: formData.payment_status,
        payment_date: formData.payment_date || null,
        payment_method: formData.payment_method || null,
        payment_notes: formData.payment_notes || null
      };

      await axios.post(`${API_URL}/sales`, payload);
      toast.success('Invoice created successfully');
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      // Handle the new detailed error response format
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.errors) {
        // New detailed error format - show each error
        const errorCount = errorDetail.errors.length;
        toast.error(`Invoice creation failed: ${errorCount} item(s) have issues`, {
          duration: 5000
        });
        
        // Show individual errors (limit to first 5 to avoid toast spam)
        errorDetail.errors.slice(0, 5).forEach((err, idx) => {
          setTimeout(() => {
            if (err.type === 'insufficient_stock') {
              toast.error(`${err.product}: Available ${err.available}, need ${err.requested} (Batch: ${err.batch})`, {
                duration: 6000
              });
            } else if (err.type === 'batch_not_found') {
              toast.error(`Batch "${err.batch}" not found for ${err.product}`, {
                duration: 6000
              });
            } else if (err.type === 'product_not_found') {
              toast.error(err.message, { duration: 6000 });
            } else {
              toast.error(err.message, { duration: 6000 });
            }
          }, idx * 500); // Stagger toasts
        });
        
        if (errorCount > 5) {
          setTimeout(() => {
            toast.warning(`...and ${errorCount - 5} more issue(s). Check inventory before proceeding.`, {
              duration: 5000
            });
          }, 3000);
        }
      } else if (typeof errorDetail === 'string') {
        // Simple string error message
        toast.error(errorDetail);
      } else {
        toast.error('Failed to create invoice. Please check stock availability.');
      }
    }
  };

  const handleViewInvoice = async (saleId) => {
    console.log('handleViewInvoice called with:', saleId);
    try {
      const [invoiceRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/sales/${saleId}/invoice-data`),
        axios.get(`${API_URL}/sales/${saleId}/transaction-history`)
      ]);
      console.log('Invoice data loaded:', invoiceRes.data);
      setCurrentInvoice(invoiceRes.data);
      setTransactionHistory(historyRes.data.history || []);
      setIsInvoiceOpen(true);
      console.log('Dialog should be open now');
    } catch (error) {
      console.error('Failed to load invoice:', error);
      toast.error('Failed to load invoice');
    }
  };

  const handlePrint = () => {
    const printContent = invoiceRef.current;
    if (!printContent) {
      toast.error('Invoice content not loaded');
      return;
    }
    
    // Open a new window for print
    const printWindow = window.open('', 'PrintInvoice', 'width=800,height=900,scrollbars=yes');
    
    if (!printWindow) {
      toast.error('Pop-up blocked. Please allow pop-ups for this site.');
      return;
    }
    
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${currentInvoice?.invoice?.invoice_number || 'Print'}</title>
        <meta charset="utf-8">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', -apple-system, sans-serif; padding: 15px; color: #1e293b; line-height: 1.4; background: white; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; margin: 8px 0; }
          th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; font-size: 11px; }
          th { background-color: #0d9488 !important; color: white !important; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .text-right { text-align: right; }
          .font-bold { font-weight: 700; }
          .font-semibold { font-weight: 600; }
          .font-medium { font-weight: 500; }
          .text-xs { font-size: 10px; }
          .text-sm { font-size: 11px; }
          .text-base { font-size: 13px; }
          .text-lg { font-size: 15px; }
          .text-xl { font-size: 18px; }
          .text-2xl { font-size: 20px; }
          .mb-1 { margin-bottom: 2px; }
          .mb-2 { margin-bottom: 4px; }
          .mb-4 { margin-bottom: 12px; }
          .mt-1 { margin-top: 2px; }
          .mt-2 { margin-top: 4px; }
          .mt-8 { margin-top: 24px; }
          .pt-1 { padding-top: 2px; }
          .pt-2 { padding-top: 4px; }
          .pt-4 { padding-top: 12px; }
          .pb-4 { padding-bottom: 12px; }
          .p-2 { padding: 6px; }
          .p-3 { padding: 10px; }
          .py-1 { padding-top: 2px; padding-bottom: 2px; }
          .space-y-1 > * + * { margin-top: 4px; }
          .border-b-2 { border-bottom: 2px solid #0d9488; }
          .border-t { border-top: 1px solid #e2e8f0; }
          .border-t-2 { border-top: 2px solid #0d9488; }
          .rounded { border-radius: 4px; }
          .rounded-lg { border-radius: 6px; }
          .bg-slate-50 { background-color: #f8fafc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .grid { display: grid; }
          .grid-cols-2 { grid-template-columns: 1fr 1fr; }
          .gap-3 { gap: 10px; }
          .gap-6 { gap: 20px; }
          .tabular-nums { font-variant-numeric: tabular-nums; }
          .text-teal-600, .text-teal-700 { color: #0d9488; }
          .text-slate-500 { color: #64748b; }
          .text-slate-600 { color: #475569; }
          .text-slate-700 { color: #334155; }
          .text-slate-900 { color: #0f172a; }
          .uppercase { text-transform: uppercase; }
          .tracking-wider { letter-spacing: 0.05em; }
          .leading-relaxed { line-height: 1.5; }
          .print-btn { 
            display: block; 
            margin: 15px auto; 
            padding: 10px 24px; 
            background: #0d9488; 
            color: white; 
            border: none; 
            border-radius: 6px; 
            font-size: 14px; 
            font-weight: 600; 
            cursor: pointer; 
          }
          .print-btn:hover { background: #0f766e; }
          .no-print { display: block; }
          .logo-container { width: 50px; height: 50px; border-radius: 6px; overflow: hidden; flex-shrink: 0; border: 1px solid #e2e8f0; }
          .logo-container img { width: 100%; height: 100%; object-fit: cover; }
          .flex { display: flex; }
          .items-start { align-items: flex-start; }
          .flex-shrink-0 { flex-shrink: 0; }
          img { max-width: 100%; height: auto; }
          .w-14 { width: 56px; }
          .h-14 { height: 56px; }
          @media print {
            body { padding: 10px; font-size: 10px; }
            .no-print { display: none !important; }
            @page { margin: 0.5cm; size: A4; }
            th, td { padding: 4px 6px; font-size: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="text-align: center; margin-bottom: 15px; padding: 12px; background: #f0fdf4; border-radius: 6px;">
          <p style="margin-bottom: 8px; color: #166534; font-size: 13px;">Click the button below or use <strong>Ctrl+P</strong> (Windows) / <strong>Cmd+P</strong> (Mac)</p>
          <button class="print-btn" onclick="window.print()">🖨️ Print Invoice</button>
          <p style="margin-top: 8px; font-size: 11px; color: #666;">Tip: Select "Save as PDF" in the print dialog to download as PDF</p>
        </div>
        ${printContent.innerHTML}
      </body>
      </html>
    `;
    
    // Safe alternative to document.write() — build the HTML as a Blob and load it
    // into the popup via an object URL. Avoids the XSS vector inherent in document.write
    // (which parses raw markup into the target document). Also handles the popup lifecycle.
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    printWindow.location.href = url;
    printWindow.focus();
    // Revoke the object URL after the popup loads to release memory
    setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch (revokeErr) { /* noop */ }
    }, 30000);
  };

  const handleDownloadPDF = async () => {
    if (!currentInvoice?.invoice?.id) {
      toast.error('Invoice data not loaded');
      return;
    }
    
    setPdfLoading(true);
    toast.info('Generating PDF...');
    
    try {
      // Use server-side PDF generation for reliability
      const response = await axios.get(
        `${API_URL}/sales/${currentInvoice.invoice.id}/download-pdf`,
        { responseType: 'blob' }
      );
      
      // Get filename from header or use invoice number
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${currentInvoice.invoice.invoice_number}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) fileName = match[1];
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      toast.success(`Downloaded: ${fileName}`);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('PDF download failed. Try using Print option instead.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!selectedSale) return;
    
    try {
      await axios.put(`${API_URL}/sales/${selectedSale.id}/payment`, paymentData);
      toast.success('Payment status updated');
      setIsPaymentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const openPaymentDialog = (sale) => {
    setSelectedSale(sale);
    setPaymentData({
      payment_status: sale.payment_status || 'pending',
      payment_date: sale.payment_date || '',
      payment_method: sale.payment_method || '',
      payment_notes: sale.payment_notes || ''
    });
    setIsPaymentDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This will restore the inventory stock.')) return;
    try {
      await axios.delete(`${API_URL}/sales/${id}`);
      toast.success('Invoice deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  // Credit Note handlers
  const openCreateCreditNote = (sale) => {
    setSelectedSaleForCN(sale);
    setCnFormData({
      items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }],
      reason: 'Customer return',
      notes: ''
    });
    setIsCreditNoteDialogOpen(true);
  };

  const handleCNItemChange = (index, field, value) => {
    const newItems = [...cnFormData.items];
    newItems[index][field] = value;
    
    if (field === 'product_id' && selectedSaleForCN) {
      const invoiceItem = selectedSaleForCN.items.find(i => i.product_id === value);
      if (invoiceItem) {
        newItems[index].batch_number = invoiceItem.batch_number;
        newItems[index].selling_price = invoiceItem.selling_price;
        newItems[index].gst_percentage = invoiceItem.gst_percentage;
      }
    }
    
    setCnFormData({ ...cnFormData, items: newItems });
  };

  const addCNItem = () => {
    setCnFormData({
      ...cnFormData,
      items: [...cnFormData.items, { product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }]
    });
  };

  const removeCNItem = (index) => {
    if (cnFormData.items.length > 1) {
      setCnFormData({
        ...cnFormData,
        items: cnFormData.items.filter((_, i) => i !== index)
      });
    }
  };

  const handleCreateCreditNote = async (e) => {
    e.preventDefault();
    
    const validItems = cnFormData.items.filter(item => 
      item.product_id && item.quantity && item.selling_price
    );
    
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/credit-notes`, {
        invoice_id: selectedSaleForCN.id,
        items: validItems.map(item => ({
          product_id: item.product_id,
          batch_number: item.batch_number,
          quantity: parseInt(item.quantity),
          selling_price: parseFloat(item.selling_price),
          gst_percentage: parseFloat(item.gst_percentage)
        })),
        reason: cnFormData.reason,
        notes: cnFormData.notes
      });
      
      toast.success('Credit note created successfully');
      setIsCreditNoteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create credit note');
    }
  };

  const openViewCreditNotes = async (sale) => {
    setSelectedSaleForCN(sale);
    try {
      const response = await axios.get(`${API_URL}/credit-notes/by-invoice/${sale.id}`);
      setInvoiceCreditNotes(response.data);
      setIsViewCreditNotesOpen(true);
    } catch (error) {
      toast.error('Failed to fetch credit notes');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      invoice_date: new Date().toISOString().split('T')[0],
      items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12, buyer_item_code: '' }],
      payment_status: 'pending',
      payment_date: '',
      payment_method: '',
      payment_notes: ''
    });
  };

  const filteredSales = sales.filter(s =>
    s.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals for form
  const calculateTotals = () => {
    let totalTaxable = 0;
    let totalGst = 0;
    formData.items.forEach(item => {
      if (item.quantity && item.selling_price) {
        const taxable = parseFloat(item.quantity) * parseFloat(item.selling_price);
        const gst = taxable * (parseFloat(item.gst_percentage || 0) / 100);
        totalTaxable += taxable;
        totalGst += gst;
      }
    });
    return { totalTaxable, totalGst, grandTotal: totalTaxable + totalGst };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Sales & Invoices</h1>
          <p className="page-subtitle">Create and manage GST sales invoices</p>
        </div>
        {isAdmin && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { resetForm(); setPickerSelections({}); setInventorySearch(''); setIsInventoryPickerOpen(true); }}
            disabled={inventory.length === 0}
            data-testid="from-inventory-btn"
          >
            <Warehouse className="w-4 h-4 mr-1" />
            From Inventory
          </Button>
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="btn-primary"
            data-testid="create-invoice-btn"
            disabled={customers.length === 0 || products.length === 0}
          >
            <Plus className="w-4 h-4" />
            Create Invoice
          </Button>
        </div>
        )}
      </div>

      {/* Warning */}
      {(customers.length === 0 || inventory.length === 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">
              {customers.length === 0 && 'Please add customers first. '}
              {inventory.length === 0 && 'No inventory available. Please add purchases first.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="sales-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {filteredSales.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" />
              <p className="text-slate-500 font-medium">No invoices found</p>
              <p className="text-sm text-slate-400 mt-1">Create your first invoice to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="professional-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => (
                    <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`}>
                      <TableCell>
                        <span className="font-mono text-sm font-semibold text-teal-700 bg-teal-50 px-2 py-1 rounded">
                          {sale.invoice_number}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(sale.invoice_date)}</TableCell>
                      <TableCell className="font-medium text-slate-900">{sale.customer_name}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-slate-900">
                        {formatCurrency(sale.grand_total)}
                      </TableCell>
                      <TableCell className="text-center">
                        <PaymentStatusBadge status={sale.payment_status || 'pending'} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewInvoice(sale.id)}
                            data-testid={`view-invoice-${sale.id}`}
                            title="View Invoice"
                          >
                            <Eye className="w-4 h-4 text-teal-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPaymentDialog(sale)}
                            data-testid={`payment-${sale.id}`}
                            title="Update Payment"
                          >
                            <CreditCard className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCreateCreditNote(sale)}
                            data-testid={`create-cn-${sale.id}`}
                            title="Create Credit Note"
                          >
                            <RotateCcw className="w-4 h-4 text-amber-600" />
                          </Button>
                          {sale.credit_notes && sale.credit_notes.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewCreditNotes(sale)}
                              data-testid={`view-cn-${sale.id}`}
                              title="View Credit Notes"
                              className="relative"
                            >
                              <ExternalLink className="w-4 h-4 text-purple-600" />
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-600 text-white text-[10px] rounded-full flex items-center justify-center">
                                {sale.credit_notes.length}
                              </span>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(sale.id)}
                            data-testid={`delete-sale-${sale.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <Select
                    value={formData.customer_id}
                    onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="sale-customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invoice_date">Invoice Date *</Label>
                  <Input
                    id="invoice_date"
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    required
                    data-testid="sale-date-input"
                  />
                </div>
              </div>

              {/* Items */}
              <div className="space-y-3">
                {/* Quick Add Product */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    Line Items
                  </Label>
                  <QuickAddProduct
                    products={products}
                    inventory={inventory}
                    onAddProduct={handleQuickAddProduct}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {formData.items.filter(i => i.product_id).length} item(s) added
                  </span>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleExcelImport}
                      accept=".xlsx,.xls"
                      className="hidden"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={downloadInvoiceTemplate}
                      title="Download Excel Template"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-1" />
                      Template
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => { setPickerSelections({}); setInventorySearch(''); setIsInventoryPickerOpen(true); }}
                      title="Pick items from inventory"
                      data-testid="pick-from-inventory-btn"
                    >
                      <Warehouse className="w-4 h-4 mr-1" />
                      From Inventory
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => fileInputRef.current?.click()}
                      title="Import items from Excel"
                      data-testid="import-excel-btn"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Import Excel
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="w-4 h-4 mr-1" /> Add Row
                    </Button>
                  </div>
                </div>
                
                {formData.items.map((item, index) => {
                  const selectedProduct = products.find(p => p.id === item.product_id);
                  return (
                  <Card key={index} className="border border-slate-200">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-7 gap-3">
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs flex items-center justify-between">
                            <span>Product</span>
                            <button
                              type="button"
                              onClick={() => openGridSelector(index)}
                              className="text-teal-600 hover:text-teal-700 flex items-center gap-1 text-[10px] font-medium"
                              data-testid={`open-grid-${index}`}
                            >
                              <Grid className="w-3 h-3" />
                              Size Grid
                            </button>
                          </Label>
                          <div className="flex gap-1">
                            <Select
                              value={item.product_id}
                              onValueChange={(value) => handleProductChange(index, value)}
                            >
                              <SelectTrigger data-testid={`item-product-${index}`} className="flex-1">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {products.map((product) => (
                                  <SelectItem key={product.id} value={product.id}>
                                    {product.name} {product.size && `(${product.size})`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {selectedProduct && (
                            <p className="text-[10px] text-slate-500 truncate">
                              {selectedProduct.brand} • {selectedProduct.size}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Batch</Label>
                          <Select
                            value={item.batch_number}
                            onValueChange={(value) => updateItem(index, 'batch_number', value)}
                            disabled={!item.product_id}
                          >
                            <SelectTrigger data-testid={`item-batch-${index}`}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableBatches(item.product_id).map((batch) => (
                                <SelectItem key={batch.batch_number} value={batch.batch_number}>
                                  {batch.batch_number} ({batch.quantity})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs" title="Buyer's procurement / indent reference — auto-filled from the Buyer Item Master. Edit to override for this invoice line only.">Buyer Ref</Label>
                          <Input
                            type="text"
                            value={item.buyer_item_code || ''}
                            onChange={(e) => updateItem(index, 'buyer_item_code', e.target.value)}
                            placeholder="—"
                            disabled={!item.product_id}
                            data-testid={`item-buyer-ref-${index}`}
                          />
                          {item.product_id && (() => {
                            const p = products.find(pp => pp.id === item.product_id);
                            const mapped = p?.buyer_item_code || '';
                            const current = (item.buyer_item_code || '').trim();
                            if (!mapped && !current) return <p className="text-[10px] text-slate-400">No mapping</p>;
                            if (mapped && current && current !== mapped) return <p className="text-[10px] text-amber-600">Overrides {mapped}</p>;
                            if (mapped && current === mapped) return <p className="text-[10px] text-slate-400">From master</p>;
                            if (!mapped && current) return <p className="text-[10px] text-amber-600">Manual entry</p>;
                            return null;
                          })()}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                // Focus back on Quick Add search
                                const quickAddInput = document.querySelector('[data-testid="quick-add-search"]');
                                if (quickAddInput) {
                                  quickAddInput.focus();
                                }
                              }
                            }}
                            className={getStockWarning(item.product_id, item.batch_number, item.quantity)?.type === 'exceed' ? 'border-red-400 ring-1 ring-red-200' : ''}
                            data-testid={`item-qty-${index}`}
                          />
                          {(() => {
                            const warning = getStockWarning(item.product_id, item.batch_number, item.quantity);
                            if (warning?.type === 'exceed') return <p className="text-[10px] text-red-600 font-medium">Exceeds stock ({warning.stock} available)</p>;
                            if (warning?.type === 'exact') return <p className="text-[10px] text-amber-600">Uses all stock</p>;
                            if (item.product_id && item.batch_number) return <p className="text-[10px] text-slate-400">Stock: {getStockForBatch(item.product_id, item.batch_number)}</p>;
                            return null;
                          })()}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Price (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.selling_price}
                            onChange={(e) => updateItem(index, 'selling_price', e.target.value)}
                            data-testid={`item-price-${index}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">GST %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="28"
                            step="0.5"
                            value={item.gst_percentage}
                            onChange={(e) => updateItem(index, 'gst_percentage', e.target.value)}
                            data-testid={`item-gst-${index}`}
                          />
                        </div>
                        <div className="flex items-end">
                          {formData.items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>

              {/* Payment Status */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                <div className="space-y-2">
                  <Label>Payment Status</Label>
                  <Select
                    value={formData.payment_status}
                    onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Totals */}
              {totals.grandTotal > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxable Amount:</span>
                    <span className="tabular-nums font-medium">{formatCurrency(totals.totalTaxable)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total GST:</span>
                    <span className="tabular-nums font-medium">{formatCurrency(totals.totalGst)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                    <span className="font-semibold text-slate-900">Grand Total:</span>
                    <span className="tabular-nums font-bold text-teal-600 text-lg">{formatCurrency(totals.grandTotal)}</span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-invoice-btn">
                Create Invoice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Update Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Payment Status</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Payment Status</Label>
              <Select
                value={paymentData.payment_status}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentData.payment_date || ''}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={paymentData.payment_method || 'none'}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={paymentData.payment_notes || ''}
                onChange={(e) => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
                placeholder="Optional payment notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdatePayment} className="btn-primary">Update Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog open={isInvoiceOpen} onOpenChange={setIsInvoiceOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Invoice Preview</span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    if (currentInvoice?.invoice?.id) {
                      handleDelete(currentInvoice.invoice.id);
                      setIsInvoiceOpen(false);
                    }
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-invoice-btn">
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleDownloadPDF} 
                  disabled={pdfLoading}
                  className="btn-primary"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-1" /> 
                  {pdfLoading ? 'Generating...' : 'Download PDF'}
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {currentInvoice && (
            <div ref={invoiceRef} className="bg-white p-6 print:p-0" id="invoice-content">
              {/* Invoice Header */}
              <div className="border-b-2 border-teal-600 pb-4 mb-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex gap-3 items-start">
                    <div className="w-14 h-14 rounded-lg overflow-hidden shadow flex-shrink-0 border border-slate-200">
                      <img 
                        src="/assets/logo.png" 
                        alt="Nirala Ennem Enterprises" 
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                      />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-teal-700">{currentInvoice.company.name}</h2>
                      <p className="text-xs text-slate-600 mt-1">2/402, First floor, VGN Nagar</p>
                      <p className="text-xs text-slate-600">Iyappanthangal, Chennai - 600056</p>
                      <p className="text-xs text-slate-600">Tamilnadu, India</p>
                      <p className="text-xs text-slate-600 mt-1"><strong>GSTIN:</strong> {currentInvoice.company.gstin}</p>
                      <p className="text-xs text-slate-600"><strong>State:</strong> {currentInvoice.company.state}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <h3 className="text-2xl font-bold text-slate-900">TAX INVOICE</h3>
                    <div className="mt-2 space-y-0.5">
                      <p className="text-xs text-slate-600">Invoice #: <span className="font-bold text-slate-900">{currentInvoice.invoice.invoice_number}</span></p>
                      <p className="text-xs text-slate-600">Date: <span className="font-semibold">{formatDate(currentInvoice.invoice.invoice_date)}</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bill To */}
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Bill To</h4>
                <p className="font-bold text-slate-900 text-base">{currentInvoice.invoice.customer_name}</p>
                <p className="text-xs text-slate-600 mt-0.5">{currentInvoice.invoice.customer_address}</p>
                <p className="text-xs text-slate-600"><strong>GSTIN:</strong> {currentInvoice.invoice.customer_gstin}</p>
              </div>

              {/* Items Table */}
              <table className="w-full border-collapse mb-4">
                <thead>
                  <tr className="bg-teal-600 text-white">
                    <th className="border border-teal-700 px-2 py-2 text-left text-xs font-semibold">#</th>
                    <th className="border border-teal-700 px-2 py-2 text-left text-xs font-semibold">Description</th>
                    <th className="border border-teal-700 px-2 py-2 text-left text-xs font-semibold">HSN</th>
                    <th className="border border-teal-700 px-2 py-2 text-left text-xs font-semibold">Batch</th>
                    <th className="border border-teal-700 px-2 py-2 text-right text-xs font-semibold">Qty</th>
                    <th className="border border-teal-700 px-2 py-2 text-right text-xs font-semibold">Rate</th>
                    <th className="border border-teal-700 px-2 py-2 text-right text-xs font-semibold">Taxable</th>
                  </tr>
                </thead>
                <tbody>
                  {currentInvoice.invoice.items.map((item, index) => {
                    // Build description: Product Name - Brand (Size)
                    const brandPart = item.brand && !item.product_name.toLowerCase().includes(item.brand.toLowerCase()) 
                      ? ` - ${item.brand}` : '';
                    const sizePart = item.size ? ` (${item.size.replace(/ x /g, '*').replace(/x/g, '*')})` : '';
                    const description = `${item.product_name}${brandPart}${sizePart}`;
                    
                    return (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="border border-slate-200 px-2 py-1.5 text-xs">{index + 1}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-xs font-medium">{description}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-xs tabular-nums">{item.hsn_code}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-xs tabular-nums">{item.batch_number}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-xs text-right tabular-nums">{item.quantity}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-xs text-right tabular-nums">{formatCurrency(item.selling_price)}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-xs text-right tabular-nums font-medium">{formatCurrency(item.taxable_amount)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* GST Summary */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Amount in Words</h4>
                  <p className="text-xs text-slate-700 font-medium bg-slate-50 p-2 rounded">{currentInvoice.amount_in_words}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs py-1">
                    <span className="text-slate-600">Taxable Amount:</span>
                    <span className="tabular-nums font-medium">{formatCurrency(currentInvoice.invoice.total_taxable)}</span>
                  </div>
                  {!currentInvoice.invoice.is_interstate ? (
                    <>
                      <div className="flex justify-between text-xs py-1">
                        <span className="text-slate-600">CGST:</span>
                        <span className="tabular-nums">{formatCurrency(currentInvoice.invoice.total_cgst)}</span>
                      </div>
                      <div className="flex justify-between text-xs py-1">
                        <span className="text-slate-600">SGST:</span>
                        <span className="tabular-nums">{formatCurrency(currentInvoice.invoice.total_sgst)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-xs py-1">
                      <span className="text-slate-600">IGST:</span>
                      <span className="tabular-nums">{formatCurrency(currentInvoice.invoice.total_igst)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t-2 border-teal-600">
                    <span className="font-bold text-slate-900">Grand Total:</span>
                    <span className="tabular-nums font-bold text-teal-600 text-base">{formatCurrency(currentInvoice.invoice.grand_total)}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t border-slate-200">
                <div className="text-right">
                  <p className="text-xs text-slate-600 mb-8">For {currentInvoice.company.name}</p>
                  <div className="border-t border-slate-300 pt-1 inline-block">
                    <p className="text-xs font-semibold text-slate-900">Authorized Signatory</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transaction History Section */}
          {transactionHistory.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-slate-900">Transaction History</h3>
              </div>
              
              <div className="space-y-2">
                {transactionHistory.map((doc, index) => (
                  <div
                    key={doc.id}
                    className={`flex items-center p-3 rounded-lg border ${
                      doc.is_current 
                        ? 'bg-teal-50 border-teal-200' 
                        : doc.document_type === 'Credit Note'
                          ? 'bg-red-50 border-red-200'
                          : doc.document_type === 'Replacement Invoice'
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    {/* Arrow connector */}
                    {index > 0 && (
                      <div className="absolute -top-4 left-6 w-0.5 h-4 bg-slate-300"></div>
                    )}
                    
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                      doc.document_type === 'Credit Note' 
                        ? 'bg-red-100' 
                        : doc.document_type === 'Replacement Invoice'
                          ? 'bg-emerald-100'
                          : 'bg-teal-100'
                    }`}>
                      {doc.document_type === 'Credit Note' ? (
                        <RotateCcw className="w-5 h-5 text-red-600" />
                      ) : doc.document_type === 'Replacement Invoice' ? (
                        <ArrowRight className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-teal-600" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${
                          doc.document_type === 'Credit Note' 
                            ? 'text-red-700' 
                            : doc.document_type === 'Replacement Invoice'
                              ? 'text-emerald-700'
                              : 'text-teal-700'
                        }`}>
                          {doc.document_number}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          doc.document_type === 'Credit Note' 
                            ? 'bg-red-100 text-red-700' 
                            : doc.document_type === 'Replacement Invoice'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-teal-100 text-teal-700'
                        }`}>
                          {doc.document_type}
                        </span>
                        {doc.is_current && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-600">
                        <span>{formatDate(doc.date)}</span>
                        <span className="font-medium">
                          {doc.document_type === 'Credit Note' ? (
                            <span className="text-red-600">-{formatCurrency(Math.abs(doc.amount))}</span>
                          ) : (
                            formatCurrency(doc.amount)
                          )}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          doc.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                          doc.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {doc.status}
                        </span>
                      </div>
                      {doc.linked_credit_note && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-purple-600">
                          <Link className="w-3 h-3" />
                          Linked to: {doc.linked_credit_note}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Credit Note Dialog */}
      <Dialog open={isCreditNoteDialogOpen} onOpenChange={setIsCreditNoteDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Create Credit Note
            </DialogTitle>
          </DialogHeader>
          
          {selectedSaleForCN && (
            <form onSubmit={handleCreateCreditNote} className="space-y-4">
              {/* Invoice Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">Invoice</p>
                    <p className="font-medium text-teal-600">{selectedSaleForCN.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Customer</p>
                    <p className="font-medium">{selectedSaleForCN.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Total</p>
                    <p className="font-medium">{formatCurrency(selectedSaleForCN.grand_total)}</p>
                  </div>
                </div>
              </div>

              {/* Reason & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reason *</Label>
                  <Select value={cnFormData.reason} onValueChange={(v) => setCnFormData({ ...cnFormData, reason: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CREDIT_NOTE_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={cnFormData.notes}
                    onChange={(e) => setCnFormData({ ...cnFormData, notes: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              {/* Items */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Items to Return/Credit</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addCNItem}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                
                {cnFormData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-amber-50 rounded-lg">
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Product</Label>
                      <Select
                        value={item.product_id}
                        onValueChange={(v) => handleCNItemChange(index, 'product_id', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedSaleForCN.items.map((invItem) => (
                            <SelectItem key={invItem.product_id} value={invItem.product_id}>
                              {invItem.product_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Batch</Label>
                      <Input value={item.batch_number} readOnly className="bg-amber-100/50" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleCNItemChange(index, 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Price</Label>
                      <Input value={item.selling_price} readOnly className="bg-amber-100/50" />
                    </div>
                    <div className="col-span-1 space-y-1">
                      <Label className="text-xs">GST%</Label>
                      <Input value={item.gst_percentage} readOnly className="bg-amber-100/50" />
                    </div>
                    <div className="col-span-1">
                      {cnFormData.items.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCNItem(index)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreditNoteDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">Create Credit Note</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Credit Notes Dialog */}
      <Dialog open={isViewCreditNotesOpen} onOpenChange={setIsViewCreditNotesOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Credit Notes for Invoice
            </DialogTitle>
          </DialogHeader>
          
          {selectedSaleForCN && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Invoice</p>
                <p className="font-medium text-teal-600">{selectedSaleForCN.invoice_number}</p>
              </div>

              {invoiceCreditNotes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <RotateCcw className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>No credit notes for this invoice</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Credit Note #</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Reason</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                      <TableHead className="text-xs">Replacement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceCreditNotes.map((cn) => (
                      <TableRow key={cn.id}>
                        <TableCell className="font-medium text-purple-600">{cn.credit_note_number}</TableCell>
                        <TableCell className="text-sm">{formatDate(cn.credit_note_date)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            cn.reason === 'Defective' ? 'bg-red-100 text-red-700' :
                            cn.reason === 'Exchange' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {cn.reason}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums font-medium text-red-600">
                          -{formatCurrency(cn.grand_total)}
                        </TableCell>
                        <TableCell>
                          {cn.replacement_invoice_number ? (
                            <span className="text-xs text-emerald-600 font-medium">{cn.replacement_invoice_number}</span>
                          ) : (
                            <span className="text-xs text-slate-400">None</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-sm text-slate-600">Total Credit:</span>
                <span className="font-bold text-red-600">
                  -{formatCurrency(invoiceCreditNotes.reduce((sum, cn) => sum + cn.grand_total, 0))}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Variant Grid Selector */}
      <VariantGridSelector
        products={products}
        inventory={inventory}
        onSelect={handleGridSelect}
        selectedProductId={gridSelectorItemIndex !== null ? formData.items[gridSelectorItemIndex]?.product_id : null}
        isOpen={isGridSelectorOpen}
        onClose={() => {
          setIsGridSelectorOpen(false);
          setGridSelectorItemIndex(null);
        }}
      />

      {/* Import Errors Dialog */}
      <Dialog open={showImportErrors} onOpenChange={setShowImportErrors}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Import Errors
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            <p className="text-sm text-slate-600 mb-4">
              The following items could not be imported due to stock or data issues:
            </p>
            <div className="space-y-2">
              {importErrors.map((error, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border text-sm ${
                    error.type === 'insufficient_stock' || error.type === 'no_stock'
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : error.type === 'product_not_found' || error.type === 'batch_not_found'
                      ? 'bg-amber-50 border-amber-200 text-amber-700'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className="font-medium shrink-0">Row {error.row}:</span>
                    <span>{error.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportErrors(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Picker Modal */}
      <Dialog open={isInventoryPickerOpen} onOpenChange={(open) => { if (!open) setIsInventoryPickerOpen(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-teal-600" />
              Select from Inventory
            </DialogTitle>
            <DialogDescription>
              Pick items to add to the invoice. Only items with available stock are shown.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search product, brand, or batch..."
              value={inventorySearch}
              onChange={(e) => setInventorySearch(e.target.value)}
              className="pl-10"
              data-testid="inventory-picker-search"
            />
          </div>

          <ScrollArea className="flex-1">
            {filteredPickerInventory.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No inventory available</p>
                <p className="text-sm text-slate-400">All items are out of stock or no match found</p>
              </div>
            ) : (
              <Table className="professional-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Batch</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right w-24">Qty to Sell</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPickerInventory.map((inv) => {
                    const key = pickerKey(inv);
                    const selected = key in pickerSelections;
                    return (
                      <TableRow key={key} className={selected ? 'bg-teal-50/60' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => togglePickerItem(inv)}
                            data-testid={`picker-select-${inv.batch_number}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium text-slate-900">{inv.product_name}</span>
                            <span className="text-xs text-slate-500 ml-2">{inv.brand}</span>
                            {inv.size && <span className="text-xs text-slate-400 ml-1">({inv.size})</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">{inv.batch_number}</code>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{inv.quantity}</TableCell>
                        <TableCell className="text-right">
                          {selected ? (
                            <Input
                              type="number"
                              min="1"
                              max={inv.quantity}
                              value={pickerSelections[key]?.qty || 1}
                              onChange={(e) => updatePickerQty(inv, e.target.value)}
                              className="w-20 h-8 text-sm text-right ml-auto"
                              data-testid={`picker-qty-${inv.batch_number}`}
                            />
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </ScrollArea>

          <DialogFooter className="pt-3 border-t gap-2">
            <span className="text-sm text-slate-500 mr-auto">{Object.keys(pickerSelections).length} item(s) selected</span>
            <Button variant="outline" onClick={() => setIsInventoryPickerOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                applyPickerSelections();
                if (!isDialogOpen) {
                  resetForm();
                  setFormData(prev => ({
                    ...prev,
                    items: Object.values(pickerSelections).map(({ inv, qty }) => ({
                      product_id: inv.product_id,
                      batch_number: inv.batch_number,
                      quantity: qty.toString(),
                      selling_price: inv.selling_price || products.find(p => p.id === inv.product_id)?.selling_price || '',
                      gst_percentage: inv.gst_percentage || products.find(p => p.id === inv.product_id)?.gst_percentage || 12
                    }))
                  }));
                  setIsDialogOpen(true);
                  setIsInventoryPickerOpen(false);
                  setPickerSelections({});
                  setInventorySearch('');
                  toast.success(`Added ${Object.keys(pickerSelections).length} item(s)`);
                }
              }}
              className="btn-primary"
              disabled={Object.keys(pickerSelections).length === 0}
              data-testid="apply-inventory-selection-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Add to Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
